import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { startCheckout, BillingError, type Plan } from "@/lib/billing";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

interface UpgradeButtonProps {
  plan: Plan;
  className?: string;
  children?: React.ReactNode;
  variant?: "primary" | "secondary";
}

/**
 * Wraps the "upgrade to plan X" flow. Handles:
 *  - Not signed in → bounce to /login (intent preserved via sessionStorage)
 *  - Signed in → call create-checkout-session, redirect to Stripe
 *  - Errors surface as toasts
 */
export function UpgradeButton({ plan, className, children, variant = "primary" }: UpgradeButtonProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  async function handleClick() {
    if (!user) {
      // Stash intent so /auth/callback can resume after signup
      sessionStorage.setItem("doclens.postLoginIntent", JSON.stringify({ kind: "checkout", plan }));
      sessionStorage.setItem("doclens.postLoginRedirect", "/pricing");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      await startCheckout(plan);
      // If we get here, the redirect didn't happen — Stripe should have taken over
    } catch (err) {
      const message = err instanceof BillingError ? err.message : "Could not start checkout";
      toast.error(message);
      setLoading(false);
    }
  }

  const base =
    variant === "primary"
      ? "bg-brand text-white hover:bg-brand-700"
      : "border border-neutral-300 hover:bg-neutral-50 text-neutral-900";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={cn(
        "block text-center rounded-lg px-4 py-2.5 font-medium transition",
        base,
        loading && "opacity-60 cursor-wait",
        className,
      )}
    >
      {loading ? "Redirecting…" : children ?? `Upgrade to ${plan}`}
    </button>
  );
}
